import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from './utils/dateFormatter';
import { normalizeAffectsAirworthiness, getAirworthinessStatusFromInspections } from './utils/inspectionStatus';
import { useAccessGroups } from './ResourcesRouter.js';
import {
	Card,
	CardBody,
	CardHeader,
	Button,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	Chip,
	Input,
	Checkbox,
	Skeleton,
	Progress,
	addToast
} from '@heroui/react';
import { parseDate } from '@internationalized/date';
import { Plus, RefreshCw, Trash2, Wrench, X } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import { AddInspectionModal, UpdateInspectionModal } from './InspectionsModal.js';

// Convert YYYY-MM-DD string to CalendarDate for HeroUI DatePicker
const stringToCalendarDate = dateString => {
	if (!dateString) return null;
	try {
		// Handle YYYY-MM-DD format
		if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			return parseDate(dateString);
		}
		// Handle timestamp (seconds)
		if (typeof dateString === 'number') {
			const date = new Date(dateString * 1000);
			const yyyy = date.getFullYear();
			const mm = String(date.getMonth() + 1).padStart(2, '0');
			const dd = String(date.getDate()).padStart(2, '0');
			return parseDate(`${yyyy}-${mm}-${dd}`);
		}
		return null;
	} catch {
		return null;
	}
};

// Convert CalendarDate to YYYY-MM-DD string for API
const calendarDateToString = calendarDate => {
	if (!calendarDate) return null;
	const yyyy = calendarDate.year;
	const mm = String(calendarDate.month).padStart(2, '0');
	const dd = String(calendarDate.day).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Inspection type options
export const INSPECTION_TYPES = [
	{ value: 'calendar', label: 'By Calendar', description: 'Due every X months from inspection date' },
	{ value: 'meter', label: 'By Meter', description: 'Due every X hours on selected meter' },
	{ value: 'dueDate', label: 'By Due Date', description: 'Due on specific date' },
	{ value: 'dueMeter', label: 'By Due Meter', description: 'Due at specific meter value' }
];

// Custom preset option (always appended to server presets)
const CUSTOM_PRESET = { unique: 'custom', inspectionName: 'Custom', type: null, description: 'Define custom inspection' };

// Preset hints (from legacy — matches inspection unique field)
export const PRESET_HINTS = {
	annual: 'Due every 12 months',
	'100-hour': 'Due every 100 hours',
	'50-hour': 'Due every 50 hours',
	'pitot-static': 'Due every 24 months',
	transponder: 'Due every 24 months',
	elt: 'Due every 12 months',
	'elt-battery': 'due date',
};

// Parse dueIn string to extract numeric value and convert to days
// Handles formats: "80 hours", "1 year, 2 days", "3 months", "80.00" (just number), etc.
const parseDueIn = dueIn => {
	if (!dueIn) return { value: 0, unit: '', overdue: false };
	const dueInStr = String(dueIn);
	const overdue = dueInStr.toLowerCase().includes('overdue') || dueInStr.toLowerCase().includes('expired');

	// Extract all time components: years, months, days, hours
	let totalDays = 0;
	let hasMatch = false;

	// Match years
	const yearMatch = dueInStr.match(/([\d.]+)\s*years?/i);
	if (yearMatch) {
		totalDays += parseFloat(yearMatch[1]) * 365;
		hasMatch = true;
	}

	// Match months
	const monthMatch = dueInStr.match(/([\d.]+)\s*months?/i);
	if (monthMatch) {
		totalDays += parseFloat(monthMatch[1]) * 30;
		hasMatch = true;
	}

	// Match days
	const dayMatch = dueInStr.match(/([\d.]+)\s*days?/i);
	if (dayMatch) {
		totalDays += parseFloat(dayMatch[1]);
		hasMatch = true;
	}

	// Match hours
	const hourMatch = dueInStr.match(/([\d.]+)\s*(hours?|hrs?|h)\b/i);
	if (hourMatch) {
		// Return hours directly for meter-based inspections
		return {
			value: parseFloat(hourMatch[1]),
			unit: 'hour',
			overdue
		};
	}

	// If we matched any date components, return total days
	if (hasMatch) {
		return {
			value: totalDays,
			unit: 'day',
			overdue
		};
	}

	// Fallback: just a number without unit (e.g., "80.00" for meter-based)
	const matchNumber = dueInStr.match(/^-?([\d.]+)$/);
	if (matchNumber) {
		return {
			value: parseFloat(matchNumber[1]),
			unit: 'numeric', // special marker for unit-less values
			overdue
		};
	}

	return { value: 0, unit: '', overdue };
};

// Calculate progress percentage for inspection
const calculateProgress = inspection => {
	const { value, overdue } = parseDueIn(inspection.dueIn);

	if (overdue) return 0;

	const frequency = parseFloat(inspection.frequency) || 0;
	if (!frequency) return 100; // no interval defined

	// Meter-based: remaining value / frequency
	if (inspection.type === 'meter' || inspection.type === 'dueMeter') {
		return Math.min(100, Math.max(0, (value / frequency) * 100));
	}

	// Date-based: frequency is in months, value is already in days from parseDueIn
	const totalDays = frequency * 30; // approximate days in interval
	const remainingDays = value; // parseDueIn already converts to days

	return Math.min(100, Math.max(0, (remainingDays / totalDays) * 100));
};

// Get progress bar color class based on percentage (more saturated colors)
const getProgressColorClass = (percentage, overdue) => {
	if (overdue) return 'bg-red-500';
	if (percentage <= 25) return 'bg-red-500';
	if (percentage <= 50) return 'bg-amber-500';
	return 'bg-emerald-500';
};

function InspectionsPage() {
	const { aircraftId } = useParams();
	const navigate = useNavigate();
	const accessGroups = useAccessGroups();
	const orgUnique = getOrgUnique();

	const [aircraft, setAircraft] = React.useState(null);
	const [inspections, setInspections] = React.useState([]);
	const [counters, setCounters] = React.useState([]);
	const [inspectionPresets, setInspectionPresets] = React.useState([]);
	const [loading, setLoading] = React.useState(true);

	// Modal states
	const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
	const [isRenewModalOpen, setIsRenewModalOpen] = React.useState(false);
	const [selectedInspection, setSelectedInspection] = React.useState(null);
	const [saving, setSaving] = React.useState(false);

	// Form state - dates are CalendarDate objects for HeroUI DatePicker
	const [formData, setFormData] = React.useState({
		preset: '',
		type: 'calendar',
		inspectionName: '',
		affectsAirworthiness: true,
		issueDate: null,
		dueDate: null,
		frequency: '',
		activeCounter: '',
		counterValue: '', // current meter value at time of inspection
		counterExpirationValue: '',
		countersValue: {}
	});

	// Fetch aircraft data and inspections
	const fetchData = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		try {
			// Fetch aircraft data (includes counters)
			const aircraftResponse = await ResourcesApi.getAircraft(parseInt(aircraftId, 10));
			const aircraftData = aircraftResponse.data?.data || aircraftResponse.data || aircraftResponse;
			setAircraft(aircraftData);
			setCounters(aircraftData.counters || []);

			// Fetch inspections
			const inspectionsResponse = await ResourcesApi.getInspections(parseInt(aircraftId, 10));
			const inspectionsData = inspectionsResponse.data?.data || inspectionsResponse.data || [];
			setInspections(Array.isArray(inspectionsData) ? inspectionsData : []);
		} catch (err) {
			console.error('Failed to load data:', err);
			addToast({
				title: 'Error',
				description: 'Failed to load inspections',
				color: 'danger'
			});
		} finally {
			setLoading(false);
		}
	}, [aircraftId]);

	React.useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Fetch inspection presets from server
	React.useEffect(() => {
		const fetchPresets = async () => {
			try {
				const response = await ResourcesApi.getResourcesDropdownData();
				const data = response.data?.data || response.data || {};
				const serverPresets = data.inspectionsDefault || [];
				setInspectionPresets([...serverPresets, CUSTOM_PRESET]);
			} catch (err) {
				console.error('Failed to load inspection presets:', err);
				setInspectionPresets([CUSTOM_PRESET]);
			}
		};
		fetchPresets();
	}, []);

	// Get status color based on danger/warning flags and dueIn field
	const getStatusInfo = inspection => {
		// Check danger flag or dueIn containing "overdue"
		const isOverdue =
			inspection.danger === 1 ||
			inspection.danger === '1' ||
			inspection.danger === true ||
			inspection.isDanger === true ||
			inspection.dueIn?.toLowerCase().includes('overdue') ||
			inspection.dueIn?.toLowerCase().includes('expired');

		if (isOverdue) {
			return { color: 'danger', label: 'Overdue' };
		}

		// Check warning flag
		if (
			inspection.warning === 1 ||
			inspection.warning === '1' ||
			inspection.warning === true ||
			inspection.isWarning === true
		) {
			return { color: 'warning', label: 'Due Soon' };
		}

		return { color: 'success', label: 'OK' };
	};

	// Get current counter value for meter-based inspections
	const getCurrentCounterValue = inspection => {
		// First try counterValue from Symfony API
		if (inspection.counterValue != null) {
			return inspection.counterValue;
		}
		// Legacy PHP backend used currentCounterValue
		if (inspection.currentCounterValue != null) {
			return inspection.currentCounterValue;
		}
		// Try activeCounter.value
		if (inspection.activeCounter?.value != null) {
			return inspection.activeCounter.value;
		}
		// Fallback: find counter in counters array by activeCounter id
		const counterId = inspection.activeCounter?.id || inspection.activeCounter || inspection.activeCounterId;
		if (counterId) {
			const counter = counters.find(c => c.id === counterId || c.id === parseInt(counterId, 10));
			if (counter?.value != null) {
				return counter.value;
			}
		}
		return null;
	};

	// Get aircraft airworthiness status based on inspections (shared utility)
	const getAirworthinessStatus = () => getAirworthinessStatusFromInspections(inspections);

	// Handle preset selection
	const handlePresetChange = presetUnique => {
		const preset = inspectionPresets.find(p => p.unique === presetUnique);
		if (preset) {
			setFormData(prev => ({
				...prev,
				preset: presetUnique,
				type: preset.type || prev.type,
				inspectionName: preset.unique === 'custom' ? '' : preset.inspectionName,
				frequency: preset.frequency || ''
			}));
		}
	};

	// Handle form field changes
	const handleFormChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	// Open add modal
	const handleOpenAddModal = () => {
		// Pre-fill countersValue with current meter readings
		const initialCountersValue = {};
		counters.forEach(c => {
			if (c.value != null) initialCountersValue[c.id] = String(c.value);
		});
		setFormData({
			preset: '',
			type: 'calendar',
			inspectionName: '',
			affectsAirworthiness: true,
			issueDate: null,
			dueDate: null,
			frequency: '',
			activeCounter: '',
			counterValue: '',
			counterExpirationValue: '',
			countersValue: initialCountersValue
		});
		setIsAddModalOpen(true);
	};

	// Open renew modal
	const handleOpenRenewModal = inspection => {
		setSelectedInspection(inspection);
		// Pre-fill from inspection's stored snapshot (matches table values)
		// Fallback to current aircraft counters if no snapshot exists
		const countersValueObj = {};
		if (Array.isArray(inspection.countersValue) && inspection.countersValue.length > 0) {
			inspection.countersValue.forEach(c => {
				if (c.id != null && c.value != null) {
					countersValueObj[c.id] = String(c.value);
				}
			});
		} else {
			counters.forEach(c => {
				if (c.value != null) countersValueObj[c.id] = String(c.value);
			});
		}
		// Get activeCounter ID - can be object {id, name, ...} or just ID
		const activeCounterValue = inspection.activeCounter?.id || inspection.activeCounter || inspection.activeCounterId;
		setFormData({
			preset: inspection.unique || 'custom',
			type: inspection.type || 'calendar',
			inspectionName: inspection.inspectionName || inspection.name || '',
			affectsAirworthiness: normalizeAffectsAirworthiness(inspection.affectsAirworthiness),
			issueDate: stringToCalendarDate(inspection.issueDate),
			dueDate: stringToCalendarDate(inspection.dueDate),
			frequency: inspection.frequency || '',
			activeCounter: activeCounterValue ? String(activeCounterValue) : '',
			counterValue: getCurrentCounterValue(inspection) ?? '',
			counterExpirationValue: inspection.counterExpirationValue || '',
			countersValue: countersValueObj
		});
		setIsRenewModalOpen(true);
	};

	// Save new inspection
	const handleAddInspection = async () => {
		if (!formData.inspectionName.trim()) {
			addToast({ title: 'Error', description: 'Inspection name is required', color: 'danger' });
			return;
		}
		if ((formData.type === 'meter' || formData.type === 'dueMeter') && !formData.activeCounter) {
			addToast({ title: 'Error', description: 'Active meter is required for meter-based inspections', color: 'danger' });
			return;
		}

		setSaving(true);
		try {
			// Symfony API expects: bool for affectsAirworthiness, string dates (YYYY-MM-DD)
			const payload = {
				unique: formData.preset === 'custom' ? 'custom' : formData.preset,
				inspectionName: formData.inspectionName,
				type: formData.type,
				affectsAirworthiness: !!formData.affectsAirworthiness,
				issueDate: calendarDateToString(formData.issueDate),
				frequency: formData.frequency ? parseInt(formData.frequency, 10) : null
			};

			// Add type-specific fields
			if (formData.type === 'dueDate' && formData.dueDate) {
				payload.dueDate = calendarDateToString(formData.dueDate);
			}
			if ((formData.type === 'meter' || formData.type === 'dueMeter') && formData.activeCounter) {
				payload.activeCounterId = parseInt(formData.activeCounter, 10);
			}
			if (formData.type === 'dueMeter' && formData.counterExpirationValue) {
				payload.counterExpirationValue = parseFloat(formData.counterExpirationValue);
			}

			// Always send countersValue if any values are filled (for all inspection types)
			const countersArray = Object.entries(formData.countersValue)
				.filter(([, value]) => value !== '' && value != null)
				.map(([id, value]) => ({
					id: parseInt(id, 10),
					value: parseFloat(value)
				}));
			if (countersArray.length > 0) {
				payload.countersValue = countersArray;
			}

			await ResourcesApi.addInspection(parseInt(aircraftId, 10), payload);
			addToast({ title: 'Success', description: 'Inspection added successfully', color: 'success' });
			setIsAddModalOpen(false);
			fetchData();
		} catch (err) {
			const status = err.response?.status;
			addToast({
				title: 'Error',
				description: status === 422 ? 'Please fill in all required fields' : 'Failed to add inspection',
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Renew inspection
	const handleRenewInspection = async () => {
		if (!selectedInspection) return;

		if ((formData.type === 'meter' || formData.type === 'dueMeter') && !formData.activeCounter) {
			addToast({ title: 'Error', description: 'Active meter is required for meter-based inspections', color: 'danger' });
			return;
		}

		setSaving(true);
		try {
			// Symfony API expects: bool for affectsAirworthiness, string dates (YYYY-MM-DD)
			const payload = {
				unique: selectedInspection.unique || 'custom',
				inspectionName: formData.inspectionName,
				type: formData.type,
				affectsAirworthiness: !!formData.affectsAirworthiness,
				issueDate: calendarDateToString(formData.issueDate),
				frequency: formData.frequency ? parseInt(formData.frequency, 10) : null
			};

			// Add type-specific fields
			if (formData.type === 'dueDate' && formData.dueDate) {
				payload.dueDate = calendarDateToString(formData.dueDate);
			}
			if ((formData.type === 'meter' || formData.type === 'dueMeter') && formData.activeCounter) {
				payload.activeCounterId = parseInt(formData.activeCounter, 10);
			}
			if (formData.type === 'dueMeter' && formData.counterExpirationValue) {
				payload.counterExpirationValue = parseFloat(formData.counterExpirationValue);
			}

			// Always send countersValue if any values are filled (for all inspection types)
			const countersArray = Object.entries(formData.countersValue)
				.filter(([, value]) => value !== '' && value != null)
				.map(([id, value]) => ({
					id: parseInt(id, 10),
					value: parseFloat(value)
				}));
			if (countersArray.length > 0) {
				payload.countersValue = countersArray;
			}

			await ResourcesApi.renewInspection(selectedInspection.id, payload);
			addToast({ title: 'Success', description: 'Inspection updated successfully', color: 'success' });
			setIsRenewModalOpen(false);
			setSelectedInspection(null);
			fetchData();
		} catch (err) {
			const status = err.response?.status;
			addToast({
				title: 'Error',
				description: status === 422 ? 'Please fill in all required fields' : 'Failed to update inspection',
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Delete inspection
	const handleDeleteInspection = async inspection => {
		if (!window.confirm(`Delete inspection "${inspection.inspectionName || inspection.name}"?`)) {
			return;
		}

		try {
			await ResourcesApi.deleteInspection(inspection.id);
			addToast({ title: 'Success', description: 'Inspection deleted', color: 'success' });
			fetchData();
		} catch (err) {
			addToast({
				title: 'Error',
				description: 'Failed to delete inspection',
				color: 'danger'
			});
		}
	};

	// Back navigation
	const handleBack = () => {
		navigate(`/aircraft/${aircraftId}`);
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{ label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/` },
		{ label: aircraft?.name || 'Aircraft', href: `/org/${orgUnique}/resources/aircraft/${aircraftId}` }
	];

	// Type-specific fields moved to InspectionsModal.js (TypeSpecificFields component)
	// to avoid duplication between Add and Update modals.

	// Loading skeleton
	if (loading) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<Skeleton className="h-12 w-full rounded-lg mb-4" />
				<div className="space-y-2">
					{[1, 2, 3].map(i => (
						<Skeleton key={i} className="h-16 w-full rounded-lg" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Inspections" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-600">
						<Wrench className="w-8 h-8 text-white" />
					</div>
					<div>
						<div className="flex items-center gap-3">
							<h1 className="text-2xl font-bold">{aircraft?.name || 'Aircraft'}</h1>
							{/* Airworthiness Status */}
							{getAirworthinessStatus() === 'airworthy' && (
								<Chip color="success" variant="flat" size="sm">Airworthy</Chip>
							)}
							{getAirworthinessStatus() === 'unairworthy' && (
								<Chip color="danger" variant="flat" size="sm">Unairworthy</Chip>
							)}
							{getAirworthinessStatus() === 'unknown' && (
								<Chip color="default" variant="flat" size="sm">Unknown</Chip>
							)}
						</div>
						<p className="text-default-500 text-sm">
							{aircraft?.registrationNumber}
							{(aircraft?.manufacturer || aircraft?.model) && ' · '}
							{[aircraft?.manufacturer, aircraft?.model].filter(Boolean).join(' ')}
							{' · Inspections'}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 self-end md:self-auto">
					{(accessGroups._loaded && accessGroups.inspectionManage) && (
						<Button
							color="primary"
							startContent={<Plus className="w-4 h-4" />}
							onPress={handleOpenAddModal}
						>
							Add Inspection
						</Button>
					)}
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Inspections Table */}
			<Card>
				<CardBody>
					{inspections.length === 0 ? (
						<div className="text-center py-12 text-default-500">
							No inspections defined. Click "Add Inspection" to create one.
						</div>
					) : (
						<Table
						aria-label="Inspections table"
						removeWrapper
						classNames={{
							tbody: '[&>tr:nth-child(even)>td]:bg-default-100 [&>tr:nth-child(even)>td:first-child]:rounded-l-lg [&>tr:nth-child(even)>td:last-child]:rounded-r-lg'
						}}
					>
							<TableHeader>
								<TableColumn>NAME</TableColumn>
								<TableColumn>INSPECTION DATE/METER</TableColumn>
								<TableColumn>DUE DATE/METER</TableColumn>
								<TableColumn>CURRENT METER</TableColumn>
								<TableColumn>TIME LEFT</TableColumn>
								<TableColumn>STATUS</TableColumn>
								<TableColumn align="end">
									{(accessGroups._loaded && accessGroups.inspectionManage) ? 'ACTIONS' : ''}
								</TableColumn>
							</TableHeader>
							<TableBody>
								{inspections.map(inspection => {
									const status = getStatusInfo(inspection);
									const isDateType = inspection.type === 'calendar' || inspection.type === 'dueDate';
									const isMeterType = inspection.type === 'meter' || inspection.type === 'dueMeter';

									return (
										<TableRow key={inspection.id}>
											<TableCell>
												<span className="font-medium">{inspection.inspectionName || inspection.name}</span>
											</TableCell>
											<TableCell>
												{isDateType && inspection.issueDate
													? formatDate(inspection.issueDate)
													: isMeterType
														? (() => {
															const activeId = inspection.activeCounter?.id || inspection.activeCounter || inspection.activeCounterId;
															if (activeId && Array.isArray(inspection.countersValue)) {
																const match = inspection.countersValue.find(c => String(c.id) === String(activeId));
																if (match) return match.value ?? '-';
															}
															return inspection.counterValue ?? '-';
														})()
														: '-'}
											</TableCell>
											<TableCell>
												{inspection.dueDate
													? formatDate(inspection.dueDate)
													: inspection.counterExpirationValue
														? String(inspection.counterExpirationValue)
														: '-'}
											</TableCell>
											<TableCell>
												{isMeterType
													? (() => {
														const activeId = inspection.activeCounter?.id || inspection.activeCounter || inspection.activeCounterId;
														if (activeId) {
															const current = counters.find(c => String(c.id) === String(activeId));
															if (current?.value != null) return current.value;
														}
														return getCurrentCounterValue(inspection) ?? '-';
													})()
													: '-'}
											</TableCell>
											<TableCell>
												<div className="flex flex-col gap-1 min-w-[120px]">
													<span className="text-sm">{inspection.dueIn || '-'}</span>
													{inspection.dueIn && (
														<Progress
															size="sm"
															value={calculateProgress(inspection)}
															className="max-w-[120px]"
															classNames={{
																track: 'bg-default-200',
																indicator: getProgressColorClass(
																	calculateProgress(inspection),
																	inspection.dueIn?.toLowerCase().includes('overdue') || inspection.dueIn?.toLowerCase().includes('expired')
																)
															}}
															aria-label="Time remaining"
														/>
													)}
												</div>
											</TableCell>
											<TableCell>
												<Chip size="sm" color={status.color} variant="flat">
													{status.label}
												</Chip>
											</TableCell>
											<TableCell>
												{(accessGroups._loaded && accessGroups.inspectionManage) && (
													<div className="flex gap-1 justify-end">
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color="primary"
															onPress={() => handleOpenRenewModal(inspection)}
														>
															<RefreshCw className="w-4 h-4" />
														</Button>
														<Button
															isIconOnly
															size="sm"
															variant="light"
															color="danger"
															onPress={() => handleDeleteInspection(inspection)}
														>
															<Trash2 className="w-4 h-4" />
														</Button>
													</div>
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</CardBody>
			</Card>

			{/* Add Inspection Modal */}
			<AddInspectionModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				formData={formData}
				saving={saving}
				inspectionPresets={inspectionPresets}
				counters={counters}
				handlePresetChange={handlePresetChange}
				handleFormChange={handleFormChange}
				handleAddInspection={handleAddInspection}
			/>

			{/* Update Inspection Modal */}
			<UpdateInspectionModal
				isOpen={isRenewModalOpen}
				onClose={() => setIsRenewModalOpen(false)}
				formData={formData}
				saving={saving}
				inspectionPresets={inspectionPresets}
				counters={counters}
				handleFormChange={handleFormChange}
				handleRenewInspection={handleRenewInspection}
			/>
		</div>
	);
}

export default InspectionsPage;
